/**
 * Crossing from one language of a book to another, without losing your place.
 *
 * A translation is a separate book to this app: its own id, its own narration,
 * its own alignment. That is right for everything except the one moment a
 * reader cares about, which is realising that the voice is speaking the wrong
 * language. Going back to the grid to hunt for the twin cover is a punishment
 * for the app's filing system, so the crossing lives here, in the header, next
 * to the title it applies to.
 *
 * Two things it does beyond navigating:
 *
 *   It states what is on the other side. An edition with no narration is a
 *   different offer from one that reads aloud, and finding that out after
 *   crossing is finding out too late.
 *
 *   It carries the position over, proportionally, but only into an edition you
 *   have never opened. A real position on the far side is worth more than an
 *   estimate, and overwriting one to be helpful would be a way of losing hours
 *   of listening.
 */

import { useCallback, useState } from "react";

import { langLabel, t, useUiLang } from "../i18n";
import { api } from "../lib/api";
import { setReadingLang } from "../lib/lang";
import { navigate } from "../lib/route";
import type { Edition } from "../lib/types";

/** Speaker with waves: this edition is narrated. */
function AudioMark({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M11 5 6 9H3v6h3l5 4V5Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path
        d="M15.5 8.5a5 5 0 0 1 0 7"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface Props {
  editions: Edition[];
  current: string;
  /** Where the reader is now, as a share of the book. Null when nowhere yet. */
  fraction: number | null;
}

export function EditionSwitch({ editions, current, fraction }: Props) {
  useUiLang();
  const [busy, setBusy] = useState("");

  const cross = useCallback(
    async (target: Edition) => {
      if (target.id === current || busy) return;
      setBusy(target.id);
      // The preference is set from the crossing itself: choosing a language
      // here is the clearest statement of one a reader ever makes, and the
      // grid should not go on offering the other one afterwards.
      setReadingLang(target.lang);
      try {
        if (fraction !== null && fraction > 0.01 && target.sentence_count > 0) {
          const existing = await api.progress(target.id).catch(() => null);
          if (!existing) {
            const sentence = Math.min(
              target.sentence_count - 1,
              Math.max(0, Math.round(fraction * target.sentence_count)),
            );
            // Seconds are left at zero: two narrations of one book are not the
            // same length, and a sentence number is a claim we can honour.
            await api.saveProgress(target.id, sentence, 0);
          }
        }
      } catch {
        // Landing at the start of the other edition is a worse outcome than
        // landing in the middle of it, and a better one than not crossing.
      }
      navigate({ name: "book", id: target.id });
      setBusy("");
    },
    [busy, current, fraction],
  );

  if (editions.length < 2) return null;

  // The edition a lone compact button crosses to below 400px. Two editions is
  // the shape this book type actually ships (see the module doc): the first
  // one that is not the current id covers that case exactly, and degrades
  // reasonably if a work ever gained a third.
  const other = editions.find((edition) => edition.id !== current) ?? editions[0];
  const otherTitle = other.has_audio
    ? t("editionSwitchNarrated", { title: other.title })
    : t("editionSwitchTextOnly", { title: other.title });

  return (
    <>
      {/* Full picture from 400px: every edition, the narration mark on the
          one you are not reading. Under 400px there is no room left for it
          once the title, the header buttons and the view toggle are counted
          (see ReaderScreen.tsx), so it hides rather than wraps to a second
          line or crowds the title further. */}
      <div
        role="group"
        aria-label={t("editionGroup")}
        className="hidden items-center gap-0.5 rounded-lg bg-[var(--color-surface)] p-0.5 min-[400px]:flex"
      >
        {editions.map((edition) => {
          const active = edition.id === current;
          return (
            <button
              key={edition.id}
              onClick={() => void cross(edition)}
              disabled={busy !== ""}
              aria-pressed={active}
              title={
                active
                  ? t("editionReading", { title: edition.title })
                  : edition.has_audio
                    ? t("editionSwitchNarrated", { title: edition.title })
                    : t("editionSwitchTextOnly", { title: edition.title })
              }
              className={
                "tap flex h-8 items-center gap-1 rounded-md px-2 text-2xs font-medium uppercase " +
                "tracking-wide transition-colors duration-150 disabled:opacity-40 " +
                (active
                  ? "bg-[var(--color-raised)] text-[var(--color-accent)]"
                  : "text-[var(--color-muted)] hover:text-[var(--color-ink)]")
              }
            >
              {edition.lang}
              {/* Only on the side you are not on. What the mark is for is the
                  decision to cross, and the edition you are already in answers
                  that question with its own transport bar at the foot of the
                  screen. Repeating it here costs width a phone does not have. */}
              {!active && edition.has_audio && <AudioMark className="h-3 w-3 opacity-70" />}
            </button>
          );
        })}
      </div>

      {/* Compact form under 400px: one 36px button, the language it would
          cross to. The narration mark cannot show in that width without
          being mistaken for decoration, so it is not lost, only moved to
          `title` and to the aria-label, exactly the wording the full group
          already uses for the same fact. */}
      <button
        onClick={() => void cross(other)}
        disabled={busy !== ""}
        aria-label={t("editionSwitchCompact", { lang: langLabel(other.lang) })}
        title={otherTitle}
        className={
          "tap flex h-9 w-9 items-center justify-center rounded-lg text-2xs font-medium uppercase " +
          "tracking-wide text-[var(--color-muted)] transition-colors duration-150 " +
          "hover:text-[var(--color-ink)] disabled:opacity-40 min-[400px]:hidden"
        }
      >
        {other.lang}
      </button>
    </>
  );
}
